import { Router } from './utils/router.js';
import { createHomePage } from './pages/home.js';
import { createAboutPage } from './pages/about.js';
import { createContactPage } from './pages/contact.js';
import { createTermsPrivacyPage } from './pages/terms-privacy.js';
import './styles/main.css';

// Global functions for window
declare global {
  interface Window {
    initializeInteractiveSteps: () => void;
    initializeTwitterCarousel: () => void;
    initializeHamburgerMenu: () => void;
    initializeSignupModal: () => void;
    openSignupModal: () => void;
    enhancedFormHandling?: () => void;
    twttr?: {
      widgets: {
        load: (element?: HTMLElement) => void;
      };
      ready?: (callback: () => void) => void;
    };
  }
}

// Intersection Observer for fade-in animations
const observerOptions: IntersectionObserverInit = {
    threshold: 0.1,
    rootMargin: '0px 0px -50px 0px'
};

const fadeInObserver = new IntersectionObserver((entries) => {
    entries.forEach(entry => {
        if (entry.isIntersecting) {
            entry.target.classList.add('visible');
        }
    });
}, observerOptions);

// Note: Navbar highlighting now handled by router for consistency

// Interactive Steps Functionality
function initializeInteractiveSteps(): void {
    const stepButtons = document.querySelectorAll('.step-btn');
    const stepItems = document.querySelectorAll('.step-item');
    const stepMediaContainer = document.querySelector('.phone-mockup-large') as HTMLElement;
    
    if (!stepButtons.length || !stepItems.length || !stepMediaContainer) return;
    
    const stepMedia = [
        { type: 'video', src: '/assets/step1.mov' }, // Step 1 - Video
        { type: 'image', src: '/assets/step2.png' }, // Step 2
        { type: 'image', src: '/assets/step3.png' }, // Step 3
        { type: 'image', src: '/assets/step4.png' }, // Step 4
        { type: 'image', src: '/assets/step5.png' }  // Step 5 - Add your image here
    ];
    
    // Preload all media for instant switching
    const mediaCache: (HTMLImageElement | HTMLVideoElement)[] = [];
    stepMedia.forEach((media, index) => {
        if (media.type === 'video') {
            const video = document.createElement('video');
            video.src = media.src;
            video.autoplay = true;
            video.loop = true;
            video.muted = true;
            video.playsInline = true;
            video.style.width = '100%';
            video.style.height = '100%';
            video.style.objectFit = 'cover';
            mediaCache[index] = video;
        } else {
            const img = new Image();
            img.src = media.src;
            img.style.width = '100%';
            img.style.height = '100%';
            img.style.objectFit = 'cover';
            mediaCache[index] = img;
        }
    });
    
    // Function to switch media
    function switchMedia(index: number) {
        if (!mediaCache[index] || !stepMediaContainer) return;
        
        // Clear current content
        stepMediaContainer.innerHTML = '';
        
        // Add new media
        const mediaElement = mediaCache[index].cloneNode(true) as HTMLImageElement | HTMLVideoElement;
        
        // If it's a video, ensure it plays
        if (mediaElement instanceof HTMLVideoElement) {
            mediaElement.play().catch(e => console.log('Video autoplay prevented:', e));
        }
        
        stepMediaContainer.appendChild(mediaElement);
    }
    
    stepButtons.forEach((btn, index) => {
        btn.addEventListener('click', () => {
            // Remove active class from all buttons and items
            stepButtons.forEach(b => b.classList.remove('active'));
            stepItems.forEach(item => item.classList.remove('active'));
            
            // Add active class to clicked button and corresponding item
            btn.classList.add('active');
            stepItems[index].classList.add('active');
            
            // Switch media
            switchMedia(index);
        });
    });
    
    // Initialize with first step
    switchMedia(0);
}

// Hamburger Menu Functionality
let hamburgerMenuListeners: (() => void)[] = [];

function initializeHamburgerMenu(): void {
    const hamburgerBtn = document.querySelector('.hamburger-btn') as HTMLButtonElement;
    const mobileNavMenu = document.querySelector('.mobile-nav-menu') as HTMLDivElement;
    const mobileNavLinks = document.querySelectorAll('.mobile-nav-link') as NodeListOf<HTMLAnchorElement>;
    
    if (!hamburgerBtn || !mobileNavMenu) {
        console.warn('Hamburger menu elements not found');
        return;
    }

    // Clean up previous event listeners
    hamburgerMenuListeners.forEach(cleanup => cleanup());
    hamburgerMenuListeners = [];

    // Toggle mobile menu function
    const toggleMenu = function() {
        const isOpen = hamburgerBtn.classList.contains('active');
        
        if (isOpen) {
            // Close menu
            hamburgerBtn.classList.remove('active');
            mobileNavMenu.classList.remove('active');
            hamburgerBtn.setAttribute('aria-expanded', 'false');
            document.body.classList.remove('mobile-nav-open');
        } else {
            // Open menu
            hamburgerBtn.classList.add('active');
            mobileNavMenu.classList.add('active');
            hamburgerBtn.setAttribute('aria-expanded', 'true');
            document.body.classList.add('mobile-nav-open');
        }
    };

    // Close menu function
    const closeMenu = function() {
        hamburgerBtn.classList.remove('active');
        mobileNavMenu.classList.remove('active');
        hamburgerBtn.setAttribute('aria-expanded', 'false');
        document.body.classList.remove('mobile-nav-open');
    };

    // Add hamburger button click listener
    hamburgerBtn.addEventListener('click', toggleMenu);
    hamburgerMenuListeners.push(() => hamburgerBtn.removeEventListener('click', toggleMenu));
    
    // Add click listeners to mobile nav links
    mobileNavLinks.forEach(link => {
        link.addEventListener('click', closeMenu);
        hamburgerMenuListeners.push(() => link.removeEventListener('click', closeMenu));
    });
    
    // Close menu when clicking outside
    const outsideClickHandler = function(event: Event) {
        const target = event.target as HTMLElement;
        if (!hamburgerBtn.contains(target) && !mobileNavMenu.contains(target)) {
            closeMenu();
        }
    };
    document.addEventListener('click', outsideClickHandler);
    hamburgerMenuListeners.push(() => document.removeEventListener('click', outsideClickHandler));
    
    // Close menu on escape key
    const escapeKeyHandler = function(event: KeyboardEvent) {
        if (event.key === 'Escape') {
            closeMenu();
        }
    };
    document.addEventListener('keydown', escapeKeyHandler);
    hamburgerMenuListeners.push(() => document.removeEventListener('keydown', escapeKeyHandler));
}

// Enhanced Twitter Carousel Functionality
function initializeTwitterCarousel(): void {
    const container = document.getElementById('tweets-container');
    const slides = document.querySelectorAll<HTMLElement>('.tweet-slide');
    const indicators = document.querySelectorAll<HTMLButtonElement>('.indicator');
    
    if (!container || slides.length === 0 || indicators.length === 0) {
        console.warn('Twitter carousel elements not found');
        return;
    }

    let currentSlide = 0;
    let isTransitioning = false;
    let autoPlayInterval: ReturnType<typeof setInterval> | null = null;
    let isPaused = false;

    // Show specific slide with smooth transition
    function showSlide(index: number): void {
        if (isTransitioning || index === currentSlide) return;
        
        isTransitioning = true;
        
        // Normalize index
        index = Math.max(0, Math.min(index, slides.length - 1));
        
        // Update indicators first
        indicators.forEach((indicator, i) => {
            indicator.classList.toggle('active', i === index);
        });
        
        // Hide current slide
        if (slides[currentSlide]) {
            slides[currentSlide].classList.remove('active');
        }
        
        // Show new slide after a brief delay for smooth transition
        setTimeout(() => {
            if (slides[index]) {
                slides[index].classList.add('active');
            }
            currentSlide = index;
            
            // Load Twitter widgets for the new slide
            loadTwitterWidgets(slides[index]);
            
            setTimeout(() => {
                isTransitioning = false;
            }, 300);
        }, 100);
    }

    // Load Twitter widgets for a specific slide
    function loadTwitterWidgets(slideElement: HTMLElement): void {
        if (typeof window.twttr !== 'undefined' && window.twttr.widgets) {
            try {
                window.twttr.widgets.load(slideElement);
            } catch (error) {
                console.warn('Twitter widget loading failed:', error);
            }
        }
    }

    // Start autoplay
    function startAutoPlay(): void {
        if (autoPlayInterval) return;
        
        autoPlayInterval = setInterval(() => {
            if (!isPaused && !isTransitioning) {
                const nextSlide = (currentSlide + 1) % slides.length;
                showSlide(nextSlide);
            }
        }, 6000); // 6 seconds between transitions
    }

    // Stop autoplay
    function stopAutoPlay(): void {
        if (autoPlayInterval) {
            clearInterval(autoPlayInterval);
            autoPlayInterval = null;
        }
    }

    // Setup event listeners
    function setupEventListeners(): void {
        // Indicator click handlers
        indicators.forEach((indicator, index) => {
            indicator.addEventListener('click', (e) => {
                e.preventDefault();
                if (!isTransitioning) {
                    showSlide(index);
                }
            });
        });

        // Pause on hover
        if (container) {
            container.addEventListener('mouseenter', () => {
                isPaused = true;
            });

            container.addEventListener('mouseleave', () => {
                isPaused = false;
            });
        }

        // Pause when tab is not visible
        document.addEventListener('visibilitychange', () => {
            if (document.hidden) {
                isPaused = true;
            } else {
                isPaused = false;
            }
        });
    }

    // Initialize carousel
    function initialize(): void {
        // Ensure first slide is active
        slides.forEach((slide, index) => {
            slide.classList.toggle('active', index === 0);
        });
        
        indicators.forEach((indicator, index) => {
            indicator.classList.toggle('active', index === 0);
        });

        currentSlide = 0;
        
        // Setup event listeners
        setupEventListeners();
        
        // Load initial Twitter widgets
        setTimeout(() => {
            loadTwitterWidgets(slides[0]);
        }, 500);
        
        // Start autoplay after initial load
        setTimeout(() => {
            startAutoPlay();
        }, 2000);
    }

    // Initialize everything
    initialize();

    // Cleanup function
    function cleanup(): void {
        stopAutoPlay();
    }

    // Expose cleanup for potential external use
    (window as any).twitterCarouselCleanup = cleanup;
}

// Video Parallax Effect - Enhanced with boundary protection
function initializeVideoParallax(): void {
    const video = document.getElementById('hero-video');
    if (!video) return;

    const heroSection = video.closest('.hero-section') as HTMLElement;
    if (!heroSection) return;

    let ticking = false;

    function updateVideoPosition(): void {
        if (ticking) return;
        
        ticking = true;
        requestAnimationFrame(() => {
            const scrollY = window.pageYOffset;
            const heroRect = heroSection.getBoundingClientRect();
            const windowHeight = window.innerHeight;
            
            // Only apply parallax when hero section is visible
            if (heroRect.bottom < 0 || heroRect.top > windowHeight) {
                ticking = false;
                return;
            }
            
            // Get hero section height for boundary calculations
            const heroHeight = heroSection.offsetHeight;
            
            // Responsive parallax rates based on screen size
            let parallaxRate = -0.15; // Default for desktop
            
            if (window.innerWidth <= 480) {
                // Completely disable parallax on mobile to prevent blur
                parallaxRate = 0;
            } else if (window.innerWidth <= 768) {
                parallaxRate = -0.05; // Very gentle on tablet
            } else if (window.innerWidth <= 1024) {
                parallaxRate = -0.10; // Moderate on small desktop
            }
            
            // Calculate transform with boundaries
            const maxTransform = heroHeight * 0.1; // Limit to 10% of hero height
            let transform = scrollY * parallaxRate;
            
            // Apply boundaries to prevent covering content above
            transform = Math.max(-maxTransform, Math.min(maxTransform * 0.5, transform));
            
            // Apply transforms with GPU acceleration
            if (video) {
                video.style.transform = `translate3d(0, ${transform}px, 0)`;
                // Remove opacity manipulation to maintain full video quality
                video.style.opacity = '1';
            }
            
            ticking = false;
        });
    }

    // Enhanced debouncing for better performance
    let scrollTimeout: ReturnType<typeof setTimeout>;
    function handleScroll(): void {
        updateVideoPosition();
        
        // Clear any existing timeout
        clearTimeout(scrollTimeout);
        
        // Set a new timeout to ensure final position is set
        scrollTimeout = setTimeout(updateVideoPosition, 16);
    }

    if (!window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
        window.addEventListener('scroll', handleScroll, { passive: true });
        
        // Handle resize to recalculate on device orientation change
        window.addEventListener('resize', () => {
            clearTimeout(scrollTimeout);
            setTimeout(updateVideoPosition, 100);
        }, { passive: true });
    }
    
    // Initial position set
    updateVideoPosition();
}

// Scroll to Top functionality
function initScrollToTop(): void {
    const scrollToTopBtn = document.getElementById('scrollToTop');
    if (!scrollToTopBtn) return;

    function toggleScrollToTopButton(): void {
        if (window.pageYOffset > 300) {
            if (scrollToTopBtn) scrollToTopBtn.style.display = 'flex';
        } else {
            if (scrollToTopBtn) scrollToTopBtn.style.display = 'none';
        }
    }

    function scrollToTop(): void {
        window.scrollTo({
            top: 0,
            behavior: 'smooth'
        });
    }

    window.addEventListener('scroll', toggleScrollToTopButton, { passive: true });
    scrollToTopBtn.addEventListener('click', scrollToTop);
}

// Signup Modal functionality
function initializeSignupModal(): void {
    const modal = document.getElementById('signup-modal');
    const overlay = document.getElementById('signup-modal-overlay');
    const closeBtn = document.getElementById('signup-modal-close');
    const form = document.getElementById('signup-form') as HTMLFormElement;
    const submitBtn = document.getElementById('signup-submit') as HTMLButtonElement;
    const btnText = document.getElementById('signup-btn-text');
    const btnLoading = document.getElementById('signup-btn-loading');
    const successMessage = document.getElementById('signup-success-message');
    const formContainer = document.getElementById('signup-form-container');
    const countdown = document.getElementById('signup-countdown');

    if (!modal || !form) return;

    // Open modal function
    window.openSignupModal = function() {
        if (modal) {
            modal.style.display = 'flex';
            setTimeout(() => modal.classList.add('active'), 10);
            document.body.style.overflow = 'hidden';
        }
    };

    // Close modal function
    function closeModal() {
        if (modal) {
            modal.classList.remove('active');
            setTimeout(() => {
                if (modal) modal.style.display = 'none';
                document.body.style.overflow = 'auto';
                // Reset form state
                if (form) form.reset();
                if (formContainer) formContainer.style.display = 'block';
                if (successMessage) successMessage.style.display = 'none';
                if (btnText) btnText.style.display = 'inline';
                if (btnLoading) btnLoading.style.display = 'none';
                if (submitBtn) submitBtn.disabled = false;
            }, 300);
        }
    }

    // Close modal events
    if (closeBtn) closeBtn.addEventListener('click', closeModal);
    if (overlay) overlay.addEventListener('click', closeModal);

    // Escape key to close
    document.addEventListener('keydown', function(e) {
        if (e.key === 'Escape' && modal && modal.classList.contains('active')) {
            closeModal();
        }
    });

    // Form submission
    form.addEventListener('submit', function(e) {
        e.preventDefault();
        e.stopPropagation();

        // Validate social link if provided
        const socialInput = document.getElementById('signup-social') as HTMLInputElement;
        if (socialInput && socialInput.value.trim()) {
            const socialValue = socialInput.value.trim();
            // Check if it starts with http:// or https://
            if (!socialValue.startsWith('http://') && !socialValue.startsWith('https://')) {
                // Add https:// if no protocol is provided
                socialInput.value = 'https://' + socialValue;
            }
        }

        // Show loading state
        if (btnText) btnText.style.display = 'none';
        if (btnLoading) btnLoading.style.display = 'inline-flex';
        if (submitBtn) submitBtn.disabled = true;

        // Create form data
        const formData = new FormData(form);

        // Submit to Formspree
        fetch(form.action, {
            method: 'POST',
            body: formData,
            headers: {
                'Accept': 'application/json'
            }
        }).then(function(response) {
            if (response.ok) {
                // Hide form and show success message
                if (formContainer) formContainer.style.display = 'none';
                if (successMessage) successMessage.style.display = 'block';

                // Start countdown
                let seconds = 4;
                if (countdown) countdown.textContent = 'Returning in ' + seconds + ' seconds...';

                const countdownInterval = setInterval(function() {
                    seconds--;
                    if (seconds > 0) {
                        if (countdown) countdown.textContent = 'Returning in ' + seconds + ' seconds...';
                    } else {
                        clearInterval(countdownInterval);
                        closeModal();
                    }
                }, 1000);
            } else {
                throw new Error('Form submission failed');
            }
        }).catch(function(error) {
            console.error('Signup form error:', error);
            // Reset button state on error
            if (btnText) btnText.style.display = 'inline';
            if (btnLoading) btnLoading.style.display = 'none';
            if (submitBtn) submitBtn.disabled = false;
            alert('Sorry, there was an error signing you up. Please try again.');
        });

        return false;
    });
}

// Initialize everything when DOM is loaded
function initializeApp(): void {
    // Add fade-in-up class to all sections
    const sections = document.querySelectorAll('section');
    sections.forEach(section => {
        section.classList.add('fade-in-up');
        fadeInObserver.observe(section);
    });
    
    // Add fade-in-up to other elements
    const elementsToAnimate = document.querySelectorAll('.step-card, .benefit-box, .contact-card, .team-card, .info-card');
    elementsToAnimate.forEach(element => {
        element.classList.add('fade-in-up');
        fadeInObserver.observe(element);
    });
    
    // Initialize interactive features
    initializeInteractiveSteps();
    initializeTwitterCarousel();
    initializeVideoParallax();
    initScrollToTop();
    initializeSignupModal();
    
    // Smooth scrolling for navigation links
    document.querySelectorAll('a[href^="#"]').forEach(anchor => {
        anchor.addEventListener('click', function (this: HTMLAnchorElement, e: Event) {
            e.preventDefault();
            const href = this.getAttribute('href');
            if (href) {
                const target = document.querySelector(href);
                if (target) {
                    const navbar = document.querySelector('.navbar') as HTMLElement;
                    const navbarHeight = navbar ? navbar.offsetHeight : 0;
                    const targetPosition = (target as HTMLElement).offsetTop - navbarHeight - 20;
                    
                    window.scrollTo({
                        top: targetPosition,
                        behavior: 'smooth'
                    });
                }
            }
        });
    });
    
    // Add hover effects to interactive elements
    const interactiveElements = document.querySelectorAll('.benefit-box, .app-store-badge, .cta-button');
    interactiveElements.forEach(element => {
        element.addEventListener('mouseenter', function(this: HTMLElement) {
            if (!window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
                this.style.transform = 'scale(1.03)';
            }
        });
        
        element.addEventListener('mouseleave', function(this: HTMLElement) {
            this.style.transform = '';
        });
    });
    
    // Lazy load Twitter widgets if they exist
    if (typeof (window as any).twttr !== 'undefined') {
        (window as any).twttr.ready(() => {
            (window as any).twttr.widgets.load();
        });
    }
    
    // Handle window resize for responsive adjustments
    let resizeTimeout: ReturnType<typeof setTimeout>;
    window.addEventListener('resize', () => {
        clearTimeout(resizeTimeout);
        resizeTimeout = setTimeout(() => {
            // Recalculate positions if needed
        }, 250);
    });
    
    // Add performance optimization for animations
    const prefersReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
    
    if (prefersReducedMotion.matches) {
        document.body.classList.add('reduce-motion');
    }
    
    prefersReducedMotion.addEventListener('change', () => {
        if (prefersReducedMotion.matches) {
            document.body.classList.add('reduce-motion');
        } else {
            document.body.classList.remove('reduce-motion');
        }
    });
}

// Navbar scroll effect
function initNavbarScrollEffect(): void {
    const navbar = document.querySelector('.navbar') as HTMLElement;
    if (!navbar) return;

    window.addEventListener('scroll', () => {
        const scrollTop = window.pageYOffset || document.documentElement.scrollTop;
        
        // Add subtle shadow when scrolled
        if (scrollTop > 10) {
            navbar.style.boxShadow = '0 2px 20px rgba(0, 0, 0, 0.15)';
        } else {
            navbar.style.boxShadow = '0 2px 10px rgba(0, 0, 0, 0.1)';
        }
    }, { passive: true });
}

// 404 Page
function create404Page(): string {
    return `
        <div style="display: flex; flex-direction: column; align-items: center; justify-content: center; min-height: 100vh; text-align: center; padding: 2rem;">
            <h1 style="font-size: 4rem; margin-bottom: 1rem;">404</h1>
            <h2 style="margin-bottom: 1rem;">Page Not Found</h2>
            <p style="margin-bottom: 2rem;">The page you're looking for doesn't exist.</p>
            <a href="/" style="background: #000; color: white; padding: 0.75rem 1.5rem; text-decoration: none; border-radius: 0.5rem;">Go Home</a>
        </div>
    `;
}

// Make functions available globally for the router
window.initializeInteractiveSteps = initializeInteractiveSteps;
window.initializeTwitterCarousel = initializeTwitterCarousel;
window.initializeHamburgerMenu = initializeHamburgerMenu;
window.initializeSignupModal = initializeSignupModal;

// Initialize Application
document.addEventListener('DOMContentLoaded', () => {
    // Create and configure router
    const router = new Router();
    
    // Register all routes
    router.addRoute({
        path: '/',
        title: 'VibeChk - Meet Creators IRL | The App for In-Person Creative Connections',
        component: createHomePage,
        metaDescription: 'VibeChk connects creators, founders, and builders through in-person coffee chats and walk & talks, ditching cold emails for authentic face-to-face connections.',
        keywords: 'creators, founders, networking, coffee chats, in-person meetings, startup community, builders, designers, engineers'
    });
    
    router.addRoute({
        path: '/about',
        title: 'About VibeChk - Our Mission to Connect Creators IRL',
        component: createAboutPage,
        metaDescription: 'Learn about VibeChk\'s mission to help creators, founders, and builders meet face-to-face through coffee chats and walk & talks. Meet the team behind the movement.',
        keywords: 'VibeChk team, startup founders, creator community, in-person networking, about us'
    });
    
    router.addRoute({
        path: '/contact',
        title: 'Contact VibeChk - Get in Touch with the Creator Community Team',
        component: createContactPage,
        metaDescription: 'Contact the VibeChk team for questions, partnerships, or feedback about our creator community app. Reach out via email or social media.',
        keywords: 'contact VibeChk, creator community support, startup contact, feedback'
    });
    
    router.addRoute({
        path: '/terms-privacy',
        title: 'Terms & Privacy Policy - VibeChk Creator Community Legal Information',
        component: createTermsPrivacyPage,
        metaDescription: 'Read VibeChk\'s Terms of Service and Privacy Policy to understand how we protect your data and enable safe creator connections through our platform.',
        keywords: 'VibeChk terms, privacy policy, legal information, data protection, user agreements'
    });
    
    router.addRoute({
        path: '/404',
        title: 'Page Not Found - VibeChk',
        component: create404Page
    });
    
    // Initialize global app features first
    initializeApp();
    initNavbarScrollEffect();
    initializePageFunctionality();
    
    // Start the router after everything is set up
    setTimeout(() => {
        router.start();
    }, 0);
});

// Enhanced step switcher functionality
function setupStepSwitchers(): void {
    const switchers = document.querySelectorAll('.step-switcher');
    
    switchers.forEach(switcher => {
        const steps = switcher.querySelectorAll('.step');
        const contents = switcher.querySelectorAll('.step-content');
        
        steps.forEach((step, index) => {
            step.addEventListener('click', () => {
                // Remove active class from all steps and contents
                steps.forEach(s => s.classList.remove('active'));
                contents.forEach(c => c.classList.remove('active'));
                
                // Add active class to clicked step and corresponding content
                step.classList.add('active');
                if (contents[index]) {
                    contents[index].classList.add('active');
                }
                
                // Add animation effect
                if (contents[index]) {
                    const contentElement = contents[index] as HTMLElement;
                    contentElement.style.transform = 'translateX(10px)';
                    contentElement.style.opacity = '0';
                    
                    setTimeout(() => {
                        contentElement.style.transform = 'translateX(0)';
                        contentElement.style.opacity = '1';
                    }, 50);
                }
            });
        });
    });
}

// Removed problematic duplicate Twitter carousel implementation

// Add intersection observer for animations
function setupScrollAnimations(): void {
    const observerOptions = {
        threshold: 0.1,
        rootMargin: '0px 0px -50px 0px'
    };
    
    const observer = new IntersectionObserver((entries) => {
        entries.forEach(entry => {
            if (entry.isIntersecting) {
                entry.target.classList.add('animate-in');
            }
        });
    }, observerOptions);
    
    // Observe elements that should animate
    const animateElements = document.querySelectorAll(
        '.tweet-card, .contact-card, .team-card, .feature-card, .step-card'
    );
    
    animateElements.forEach(el => {
        observer.observe(el);
        el.classList.add('animate-on-scroll');
    });
}

// Initialize all functionality
function initializePageFunctionality(): void {
    setupStepSwitchers();
    setupScrollAnimations();
    
    // Add smooth scrolling for anchor links
    document.querySelectorAll('a[href^="#"]').forEach(anchor => {
        anchor.addEventListener('click', function (this: HTMLAnchorElement, e: Event) {
            e.preventDefault();
            const href = this.getAttribute('href');
            if (href && href !== '#') {
                const target = document.querySelector(href);
                if (target) {
                    target.scrollIntoView({
                        behavior: 'smooth',
                        block: 'start'
                    });
                }
            }
        });
    });
} 